/**
 * Staff Tab Component
 *
 * Displays list of staff users with ability to create, edit, and deactivate.
 * Shows role, assigned stores, and MFA status.
 *
 * Part of Phase 5: User Management Admin Page
 */

import { useState } from 'react';
import { pickStaffRole } from '../../context/PermissionContext';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Store as StoreIcon,
  Key as KeyIcon,
} from '@mui/icons-material';
import { useNotify } from 'react-admin';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import authInstance from '../../services/httpClient';
import { queryKeys, useAdminMutation, extractErrorMessage } from '../../hooks/queries';
import { StaffFormDialog } from './StaffFormDialog';
import { roleLabel, storeAccess } from './staffPresets';
import { useAdminTheme } from '../../admin-theme';

interface StaffUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  roles: string[];
  mfaEnabled: boolean;
  mfaRequired: boolean;
  pagePermissions?: string[] | null;
  tabletAdmin?: boolean;
  oauthProviders: string[];
  storeAssignments?: Array<{
    store: {
      id: string;
      name: string;
    };
  }>;
  createdAt: string;
}

export const StaffTab = () => {
  const { colors, elevatedInputStyle } = useAdminTheme();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<StaffUser | null>(null);
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState<StaffUser | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [sendCredentials, setSendCredentials] = useState(true);
  const notify = useNotify();
  const queryClient = useQueryClient();

  const { data: staff = [], isLoading: loading, error: queryError } = useQuery({
    queryKey: queryKeys.staff.list(),
    queryFn: async () => {
      const response = await authInstance.get('admin/users/staff');
      if (!response.data || typeof response.data !== 'object' || !Array.isArray(response.data.users)) {
        throw new Error(`Invalid response shape: ${JSON.stringify(response.data)?.substring(0, 100)}`);
      }
      return response.data.users as StaffUser[];
    },
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    retry: 2,
  });

  // Same query (and cache) as the form's store picker: the chips compare against every store.
  const { data: allStoreIds = [] } = useQuery({
    queryKey: queryKeys.stores.list(),
    queryFn: async () => {
      const response = await authInstance.get('admin/stores');
      const arr = Array.isArray(response.data) ? response.data : (response.data?.stores || response.data?.data || []);
      return arr as { id: string; name: string }[];
    },
    select: (stores) => stores.map((s) => s.id),
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
  });

  const error = queryError ? extractErrorMessage(queryError) : null;

  const deactivateMutation = useAdminMutation({
    mutationFn: (userId: string) => authInstance.delete(`admin/users/staff/${userId}`),
    successMessage: 'User deactivated successfully',
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: queryKeys.staff.all }); },
  });

  const resetPasswordMutation = useAdminMutation({
    mutationFn: (vars: { userId: string; password: string; sendCredentials: boolean }) =>
      authInstance.post(`admin/users/staff/${vars.userId}/reset-password`, {
        password: vars.password,
        sendCredentials: vars.sendCredentials,
      }),
    successMessage: (data: any) =>
      data?.data?.credentialsSent
        ? 'Password reset and credentials sent via email'
        : 'Password reset successfully',
    onSuccess: () => { handleResetPasswordClose(); },
  });

  const handleCreate = () => {
    setSelectedUser(null);
    setDialogOpen(true);
  };

  const handleEdit = (user: StaffUser) => {
    setSelectedUser(user);
    setDialogOpen(true);
  };

  const handleDeactivate = (userId: string) => {
    if (!window.confirm('Are you sure you want to deactivate this user? They will lose access to the system.')) {
      return;
    }
    deactivateMutation.mutate(userId);
  };

  const handleResetPassword = (user: StaffUser) => {
    setResetPasswordUser(user);
    setResetPasswordOpen(true);
  };

  const handleResetPasswordClose = () => {
    setResetPasswordOpen(false);
    setResetPasswordUser(null);
    setNewPassword('');
    setSendCredentials(true);
  };

  const handleResetPasswordSubmit = () => {
    if (!resetPasswordUser || !newPassword) return;

    if (newPassword.length < 8) {
      notify('Password must be at least 8 characters', { type: 'error' });
      return;
    }

    resetPasswordMutation.mutate({
      userId: resetPasswordUser.id,
      password: newPassword,
      sendCredentials,
    });
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedUser(null);
  };

  const handleDialogSuccess = () => {
    setDialogOpen(false);
    setSelectedUser(null);
    queryClient.invalidateQueries({ queryKey: queryKeys.staff.all });
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'owner':
        return 'error';
      case 'admin':
        return 'primary';
      case 'staff':
        return 'default';
      case 'tablet':
        return 'warning';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress sx={{ color: colors.info }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert
          severity="error"
          sx={{
            bgcolor: 'rgba(255, 0, 60, 0.1)',
            border: `1px solid ${colors.errorGlow}40`,
            borderRadius: 2,
            color: colors.textPrimary,
            '& .MuiAlert-icon': { color: colors.errorGlow },
          }}
        >
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} px={3}>
        <Typography variant="body2" sx={{ color: colors.textSecondary }}>
          <strong style={{ color: colors.textPrimary }}>{staff.length}</strong> staff users
        </Typography>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleCreate}
          sx={{
            color: colors.info,
            borderColor: `${colors.info}60`,
            bgcolor: `${colors.info}10`,
            fontWeight: 600,
            textTransform: 'none',
            '&:hover': {
              bgcolor: `${colors.info}20`,
              borderColor: colors.info,
              boxShadow: `0 0 15px ${colors.info}30`,
            },
          }}
        >
          Create Staff User
        </Button>
      </Box>

      <TableContainer
        sx={{
          bgcolor: colors.bgCard,
          borderRadius: 2,
          border: `1px solid ${colors.border}`,
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              {['Name', 'Email', 'Role', 'Assigned Stores', 'Security'].map((header) => (
                <TableCell
                  key={header}
                  sx={{
                    color: colors.textMuted,
                    borderBottom: `1px solid ${colors.border}`,
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                  }}
                >
                  {header}
                </TableCell>
              ))}
              <TableCell
                align="right"
                sx={{
                  color: colors.textMuted,
                  borderBottom: `1px solid ${colors.border}`,
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                }}
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {staff.map((user) => (
              <TableRow
                key={user.id}
                sx={{
                  '&:hover': { bgcolor: colors.bgElevated },
                  transition: 'background-color 0.2s',
                }}
              >
                <TableCell sx={{ color: colors.textPrimary, borderBottom: `1px solid ${colors.border}` }}>
                  {user.firstName} {user.lastName}
                </TableCell>
                <TableCell sx={{ color: colors.textSecondary, borderBottom: `1px solid ${colors.border}` }}>
                  {user.email}
                </TableCell>
                <TableCell sx={{ borderBottom: `1px solid ${colors.border}` }}>
                  <Chip
                    label={roleLabel(user.roles, user.pagePermissions ?? null)}
                    color={getRoleColor(pickStaffRole(user.roles) ?? user.roles[0])}
                    size="small"
                    sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                  />
                </TableCell>
                <TableCell sx={{ borderBottom: `1px solid ${colors.border}` }}>
                  {(() => {
                    const assigned = (user.storeAssignments || []).map((a) => a.store);
                    const access = storeAccess(assigned, allStoreIds);
                    const tone = access.level === 'all' ? colors.successGlow : colors.warning;
                    return (
                      <Tooltip title={assigned.length ? assigned.map((s) => s.name).join(', ') : 'Every store, including ones added later'}>
                        <Chip
                          icon={access.level === 'some' ? <StoreIcon sx={{ color: `${tone} !important` }} /> : undefined}
                          label={access.label}
                          size="small"
                          sx={{
                            bgcolor: `${tone}15`,
                            color: tone,
                            border: `1px solid ${tone}40`,
                          }}
                        />
                      </Tooltip>
                    );
                  })()}
                </TableCell>
                <TableCell sx={{ borderBottom: `1px solid ${colors.border}` }}>
                  <Box display="flex" gap={0.5} flexWrap="wrap">
                    {user.mfaEnabled ? (
                      <Chip
                        label="MFA Active"
                        size="small"
                        sx={{
                          bgcolor: `${colors.successGlow}15`,
                          color: colors.successGlow,
                          border: `1px solid ${colors.successGlow}40`,
                          fontWeight: 600,
                          fontSize: '0.7rem',
                        }}
                      />
                    ) : user.mfaRequired ? (
                      <Tooltip title="User must set up MFA on next login">
                        <Chip
                          label="MFA Required"
                          size="small"
                          sx={{
                            bgcolor: `${colors.warning}15`,
                            color: colors.warning,
                            border: `1px solid ${colors.warning}40`,
                            fontWeight: 600,
                            fontSize: '0.7rem',
                          }}
                        />
                      </Tooltip>
                    ) : null}
                    {user.pagePermissions && user.pagePermissions.length > 0 && (
                      <Tooltip title={`Sidebar limited to: ${user.pagePermissions.join(', ')}`}>
                        <Chip
                          label={`Pages: ${user.pagePermissions.length}`}
                          size="small"
                          sx={{
                            ml: 0.5,
                            bgcolor: `${colors.info}15`,
                            color: colors.info,
                            border: `1px solid ${colors.info}40`,
                            fontWeight: 600,
                            fontSize: '0.7rem',
                          }}
                        />
                      </Tooltip>
                    )}
                    {user.oauthProviders && user.oauthProviders.length > 0 && (
                      <Chip
                        label="OAuth"
                        size="small"
                        sx={{
                          bgcolor: `${colors.info}15`,
                          color: colors.info,
                          border: `1px solid ${colors.info}40`,
                          fontWeight: 600,
                          fontSize: '0.7rem',
                        }}
                      />
                    )}
                  </Box>
                </TableCell>
                <TableCell align="right" sx={{ borderBottom: `1px solid ${colors.border}` }}>
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => handleEdit(user)} sx={{ color: colors.textSecondary, '&:hover': { color: colors.info, bgcolor: `${colors.info}15` } }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Reset Password">
                    <IconButton size="small" onClick={() => handleResetPassword(user)} sx={{ color: colors.textSecondary, '&:hover': { color: colors.warning, bgcolor: `${colors.warning}15` } }}>
                      <KeyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {!user.roles.includes('owner') && (
                    <Tooltip title="Deactivate">
                      <IconButton size="small" onClick={() => handleDeactivate(user.id)} sx={{ color: colors.textSecondary, '&:hover': { color: colors.errorGlow, bgcolor: `${colors.errorGlow}15` } }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {staff.length === 0 && (
        <Box p={4} textAlign="center">
          <Typography sx={{ color: colors.textMuted }}>
            No staff users found. Create one to get started.
          </Typography>
        </Box>
      )}

      <StaffFormDialog
        open={dialogOpen}
        user={selectedUser}
        onClose={handleDialogClose}
        onSuccess={handleDialogSuccess}
      />

      {/* Reset Password Dialog */}
      <Dialog
        open={resetPasswordOpen}
        onClose={handleResetPasswordClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: colors.bgCard,
            border: `1px solid ${colors.border}`,
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle sx={{ color: colors.textPrimary, fontWeight: 700 }}>Reset Password</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 2 }}>
              Set a new password for <strong style={{ color: colors.textPrimary }}>{resetPasswordUser?.email}</strong>
            </Typography>
            <TextField
              label="New Password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              fullWidth
              required
              helperText="Minimum 8 characters. Password is permanent (no forced change on login)."
              sx={{
                mb: 2,
                '& .MuiInputBase-root': {
                  bgcolor: colors.bgElevated,
                  color: colors.textPrimary,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.info },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.info },
                },
                '& .MuiInputLabel-root': { color: colors.textSecondary, '&.Mui-focused': { color: colors.info } },
                '& .MuiFormHelperText-root': { color: colors.textMuted },
              }}
            />
            <Box sx={{
              p: 2,
              bgcolor: `${colors.info}10`,
              borderRadius: 2,
              border: `1px solid ${colors.info}30`,
            }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={sendCredentials}
                    onChange={(e) => setSendCredentials(e.target.checked)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: colors.info },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: colors.info },
                    }}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={500} sx={{ color: colors.textPrimary }}>
                      Email new credentials
                    </Typography>
                    <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                      Send the new password to the user via email
                    </Typography>
                  </Box>
                }
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleResetPasswordClose} disabled={resetPasswordMutation.isPending} sx={{ color: colors.textSecondary }}>
            Cancel
          </Button>
          <Button
            onClick={handleResetPasswordSubmit}
            variant="outlined"
            disabled={resetPasswordMutation.isPending || !newPassword}
            startIcon={resetPasswordMutation.isPending && <CircularProgress size={20} />}
            sx={{
              color: colors.info,
              borderColor: `${colors.info}60`,
              '&:hover': { bgcolor: `${colors.info}20`, borderColor: colors.info },
            }}
          >
            Reset Password
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
